import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandMark } from '../brand/BrandMark';
import { faArrowLeft, faKey, faRightFromBracket, faWifi } from '@fortawesome/free-solid-svg-icons';
import { ChangePasswordSheet } from '../auth/ChangePasswordSheet';
import { useAuth } from '../../features/auth/useAuth';
import { useOnline } from '../../hooks/useOnline';
import { ROUTES } from '../../routes/paths';
import type { User } from '../../types/api';

function initials(fullName: string): string {
  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * Estructura del panel del chofer.
 *
 * No lleva el shell del administrador a proposito: se usa con una mano, en la
 * calle, sobre un telefono. Una cabecera minima y nada mas; la accion principal
 * de cada pantalla vive abajo, al alcance del pulgar. Ver `docs/UI_STANDARDS.md`.
 *
 * La cabecera es una barra de titulo de aplicacion movil: dentro de una orden
 * se convierte en el camino de vuelta, y la vuelta esta siempre en el mismo
 * sitio. No lleva campana de avisos porque hoy los avisos se escriben solo
 * para el administrador: un contador que nunca pasa de cero ensena a no mirar.
 */
export function DriverLayout(): ReactNode {
  const { user, logout } = useAuth();
  const isOnline = useOnline();
  const isOrderDetail = useMatch(ROUTES.driver.orderDetail) !== null;

  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 bg-primary text-white shadow-[0_1px_3px_rgba(16,24,40,0.18)]">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between gap-2 px-2">
          {isOrderDetail ? (
            <Link
              to={ROUTES.driver.orders}
              className="flex min-h-11 min-w-0 items-center gap-2 rounded-card px-2 text-sm font-medium hover:bg-white/10"
            >
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" className="w-4 shrink-0" />
              <span className="truncate">Mis órdenes</span>
            </Link>
          ) : (
            <p className="flex min-w-0 items-center gap-2 px-2">
              {/*
                La marca en lugar del icono generico de camion: en la pantalla
                de inicio del chofer es lo unico que dice de que sistema es
                esto. En el detalle de una orden cede el sitio al enlace de
                volver, que ahi importa mas.
              */}
              <BrandMark className="h-6 w-auto shrink-0" />
              <span className="truncate text-sm font-semibold">Mis órdenes</span>
            </p>
          )}

          <UserMenu user={user} onLogout={() => void logout()} />
        </div>

        {/*
          El aviso de red va pegado a la cabecera y no dentro de la pantalla:
          es una condición del teléfono, no de lo que se este mirando, y ahi no
          lo tapa el desplazamiento de una lista larga.
        */}
        {isOnline ? null : (
          <p className="flex items-center justify-center gap-2 bg-warning px-4 py-1.5 text-xs font-medium text-white">
            <FontAwesomeIcon icon={faWifi} aria-hidden="true" />
            Sin conexion. Lo que confirmes ahora no se enviara.
          </p>
        )}
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Identidad, contrasena y cierre de sesion.
 *
 * Cerrar sesion vive dentro del menu y no como un icono suelto en la barra:
 * era un boton de 44 px pegado al borde de la pantalla, del tamano justo para
 * pulsarlo sin querer al agarrar el telefono, y volver a entrar a mitad de una
 * ruta significa teclear una contrasena con guantes.
 *
 * El chofer solo tiene aqui una cosa que hacer: cambiar su contrasena. No hay
 * ficha de cuenta que abrir —sus datos los administra la oficina y el no puede
 * cambiarlos—, asi que ofrecerla seria ofrecer una pantalla de solo lectura
 * que no responde a ninguna pregunta suya.
 */
function UserMenu({ user, onLogout }: { user: User; onLogout: () => void }): ReactNode {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Cuenta de ${user.full_name}`}
        className="flex h-11 items-center gap-2 rounded-card px-2 hover:bg-white/10"
      >
        <span
          aria-hidden="true"
          className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-xs font-semibold"
        >
          {initials(user.full_name)}
        </span>
        <span className="hidden max-w-32 truncate text-sm font-medium sm:block">
          {user.full_name}
        </span>
      </button>

      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-60 rounded-card border border-line bg-surface py-1 text-content shadow-lg"
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium text-content">{user.full_name}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
            <p className="mt-0.5 text-xs text-muted">Chofer</p>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              setIsChangingPassword(true);
            }}
            className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm text-content hover:bg-primary-soft"
          >
            <FontAwesomeIcon icon={faKey} aria-hidden="true" className="w-3.5" />
            Cambiar contraseña
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className="flex min-h-11 w-full items-center gap-2 border-t border-line px-3 text-left text-sm text-content hover:bg-primary-soft"
          >
            <FontAwesomeIcon icon={faRightFromBracket} aria-hidden="true" className="w-3.5" />
            Cerrar sesión
          </button>
        </div>
      ) : null}

      <ChangePasswordSheet
        isOpen={isChangingPassword}
        onClose={() => {
          setIsChangingPassword(false);
        }}
      />
    </div>
  );
}
